import React, { useState } from "react";
import OperationsPage from "./OperationsPage";
import { DayNavigator } from "../../components/shared/Navigators";
import { formatTime12 } from "../../lib/format";
import MoveDailyMenu from "../../components/shared/MoveDailyMenu";
import { rowClass as defaultRowClass } from "../../components/shared/Primitives";
import FollowUpsDueSection from "../../components/shared/FollowUpsDueSection";

const STATUS = ["Not Started", "In Progress", "Completed", "Cancelled", "Rescheduled", "No-Show"];
const PRIORITY = ["Normal", "Priority"];

const DEFAULT_FIELDS = [
  { name: "company", label: "Company" },
  { name: "individual", label: "Individual(s)" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "purpose", label: "Purpose" },
  { name: "start_time", label: "Start Time", type: "time" },
  { name: "completion_time", label: "Completion Time", type: "time" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "follow_up", label: "Follow Up", full: true },
  { name: "follow_up_date", label: "Follow Up Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUS },
  { name: "priority", label: "Priority", type: "select", options: PRIORITY },
];

const ADMIN_FIELDS = [
  { name: "task", label: "Task" },
  { name: "company", label: "Company" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "purpose", label: "Purpose" },
  { name: "start_time", label: "Start Time", type: "time" },
  { name: "completion_time", label: "Completion Time", type: "time" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "follow_up", label: "Follow Up", full: true },
  { name: "follow_up_date", label: "Follow Up Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUS },
  { name: "priority", label: "Priority", type: "select", options: PRIORITY },
];

const PROS_RES_FIELDS = [
  { name: "borrowers", label: "Borrower(s)" },
  { name: "referred_by", label: "Referred By" },
  { name: "purpose", label: "Purpose" },
  { name: "start_time", label: "Start Time", type: "time" },
  { name: "completion_time", label: "Completion Time", type: "time" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "follow_up", label: "Follow Up", full: true },
  { name: "follow_up_date", label: "Follow Up Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUS },
  { name: "priority", label: "Priority", type: "select", options: PRIORITY },
];

const INTERNAL_FIELDS = [
  { name: "task", label: "Task" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "start_time", label: "Start Time", type: "time" },
  { name: "completion_time", label: "Completion Time", type: "time" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "follow_up", label: "Follow Up", full: true },
  { name: "follow_up_date", label: "Follow Up Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUS },
  { name: "priority", label: "Priority", type: "select", options: PRIORITY },
];

const INTRO_GIVEN_FIELDS = [
  { name: "company", label: "Company" },
  { name: "individual", label: "Individual / Company" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "introduced_company", label: "Introduced Company" },
  { name: "introduced_individual", label: "Introduced Individual" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "follow_up", label: "Follow Up", full: true },
  { name: "follow_up_date", label: "Follow Up Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUS },
  { name: "priority", label: "Priority", type: "select", options: PRIORITY },
];

const INTRO_RCVD_FIELDS = [
  { name: "company", label: "Company" },
  { name: "individual", label: "Individual / Company" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "type", label: "Type" },
  { name: "referred_company", label: "Referred Company" },
  { name: "referred_individual", label: "Referred Individual / Company" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "follow_up", label: "Follow Up", full: true },
  { name: "follow_up_date", label: "Follow Up Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUS },
  { name: "priority", label: "Priority", type: "select", options: PRIORITY },
];

const FIELD_OVERRIDES = {
  "Administrative": ADMIN_FIELDS,
  "Prospects - Residential": PROS_RES_FIELDS,
  "Internal / Other": INTERNAL_FIELDS,
  "Professional Introductions Given": INTRO_GIVEN_FIELDS,
  "Professional Introductions Received": INTRO_RCVD_FIELDS,
};

const getFieldsFor = (name) => FIELD_OVERRIDES[name] || DEFAULT_FIELDS;

const CATS = [
  { key: "Meeting / Appointments", color: "#0090FF", fields: DEFAULT_FIELDS },
  { key: "Administrative", color: "#707070", fields: ADMIN_FIELDS },
  { key: "Deals in Process - Commercial", color: "#F76B15", fields: DEFAULT_FIELDS },
  { key: "Deals in Process - Residential", color: "#F76B15", fields: DEFAULT_FIELDS },
  { key: "Prospects - Commercial", color: "#FFC53D", fields: DEFAULT_FIELDS },
  { key: "Prospects - Residential", color: "#FFC53D", fields: PROS_RES_FIELDS },
  { key: "COIs", color: "#46A758", fields: DEFAULT_FIELDS },
  { key: "Realtors / Real Estate Professionals", color: "#46A758", fields: DEFAULT_FIELDS },
  { key: "Pennsylvania EXP Realtors", color: "#46A758", fields: DEFAULT_FIELDS },
  { key: "Other Real Estate Professionals", color: "#46A758", fields: DEFAULT_FIELDS },
  { key: "Provisors", color: "#8E4EC6", fields: DEFAULT_FIELDS },
  { key: "The Connective", color: "#8E4EC6", fields: DEFAULT_FIELDS },
  { key: "Other Networking", color: "#8E4EC6", fields: DEFAULT_FIELDS },
  { key: "Lending Partners - Commercial", color: "#0E7AB2", fields: DEFAULT_FIELDS },
  { key: "Lending Partners - Residential", color: "#0E7AB2", fields: DEFAULT_FIELDS },
  { key: "Fiorenza's Food for Friends", color: "#E54D2E", fields: DEFAULT_FIELDS },
  { key: "Internal / Other", color: "#707070", fields: INTERNAL_FIELDS },
  { key: "LinkedIn", color: "#0067B8", fields: DEFAULT_FIELDS },
  { key: "Professional Introductions Given", color: "#46A758", fields: INTRO_GIVEN_FIELDS },
  { key: "Professional Introductions Received", color: "#46A758", fields: INTRO_RCVD_FIELDS },
].map((c) => ({ ...c, label: c.key }));

const COLUMNS = [
  { key: "company", label: "Company" },
  { key: "individual", label: "Individual / Task", render: (v, r) => v || r.task || r.borrowers },
  { key: "purpose", label: "Purpose" },
  { key: "start_time", label: "Start", className: "font-mono-rw text-xs", render: formatTime12 },
  { key: "completion_time", label: "End", className: "font-mono-rw text-xs", render: formatTime12 },
  { key: "follow_up", label: "Follow-up" },
  { key: "status", label: "Status" },
  { key: "priority", label: "Priority" },
];

export default function DailyTodo() {
  const today = new Date().toISOString().slice(0, 10);
  const [value, setValue] = useState(today);

  // A task is "incomplete" unless it's been marked Completed/Closed.
  const isIncomplete = (r) => {
    const s = (r.status || "").toLowerCase();
    return !["completed", "closed"].includes(s);
  };

  // Row highlight rules for Daily To-Do:
  //   • completed/closed → yellow  (default)
  //   • cancelled/rescheduled/no-show → grey with strike-through (default)
  //   • priority → strong pink (default)
  //   • anything else that isn't yet complete → LIGHT PINK ("row-pending")
  // The synthetic "now" row + already-classed rows keep their existing style.
  const dailyRowClass = (r) => {
    const base = defaultRowClass(r);
    if (base) return base;
    return isIncomplete(r) ? "row-pending" : "";
  };

  return (
    <OperationsPage
      kind="daily"
      title="Daily To-Do List"
      eyebrow="Operations"
      dot="var(--rw-dot-todo)"
      description="A day-specific planner — every entry belongs to one calendar day."
      scope={{
        type: "date",
        value,
        setValue,
        Navigator: <DayNavigator value={value} onChange={setValue} />,
      }}
      defaultCategories={CATS}
      getFieldsFor={getFieldsFor}
      columns={COLUMNS}
      extraRowActions={(r, refresh) =>
        isIncomplete(r) ? <MoveDailyMenu row={r} onMoved={refresh} /> : null
      }
      rowClass={dailyRowClass}
      sortRowsBy={(a, b) => {
        // Rows with a start_time sort chronologically; rows without stay in
        // creation order at the bottom.
        const at = a.start_time || "99:99";
        const bt = b.start_time || "99:99";
        return at.localeCompare(bt);
      }}
      prependSections={({ scope, entries, refresh }) => (
        <FollowUpsDueSection
          date={scope.value}
          refreshKey={entries.length}
          refresh={refresh}
        />
      )}
    />
  );
}
